/**
 * Servicio para manejar autenticación OAuth con plataformas
 * Usa Cloud Functions para los flujos OAuth reales
 */

// Declarar tipo global para window.auth
declare global {
  interface Window {
    auth: any;
  }
}

export interface OAuthInitResponse {
  success: boolean;
  authUrl?: string;
  message: string;
  error?: string;
}

export interface OAuthCompleteResponse {
  success: boolean;
  message: string;
  platform: string;
  data?: any;
  error?: string;
}

export interface PlatformOAuthConfig {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  shopDomain?: string;
  scopes?: string[];
}

class OAuthService {
  private functions: any;
  private httpsCallable: any;

  constructor() {
    // Usar Firebase Functions desde window.auth
    if (typeof window !== 'undefined' && window.auth) {
      this.functions = window.auth.functions;
      this.httpsCallable = window.auth.httpsCallable;
      console.log('✅ OAuth Service: Firebase Functions inicializado correctamente');
    } else {
      console.warn('⚠️ OAuth Service: window.auth no disponible. Esperando inicialización...');
      // Intentar obtener después
      setTimeout(() => {
        if (window.auth) {
          this.functions = window.auth.functions;
          this.httpsCallable = window.auth.httpsCallable;
          console.log('✅ OAuth Service: Firebase Functions inicializado (retry exitoso)');
        }
      }, 1000);
    }
  }

  getRedirectUri(platform: string): string {
    const origin = window.location.origin;
    return `${origin}/oauth/callback/${platform}`;
  }

  // ============================================================================
  // MERCADOLIBRE
  // ============================================================================

  async initMercadoLibreOAuth(): Promise<OAuthInitResponse> {
    try {
      if (!this.functions || !this.httpsCallable) {
        throw new Error('Firebase Functions no está disponible');
      }

      const initOAuthMercadoLibre = this.httpsCallable(this.functions, 'initOAuthMercadoLibre');

      // No necesita parámetros - las credenciales están en el servidor
      const result = await initOAuthMercadoLibre({});

      return result.data;
    } catch (error) {
      console.error('Error iniciando OAuth MercadoLibre:', error);
      return {
        success: false,
        message: 'Error iniciando autenticación',
        error: error instanceof Error ? error.message : 'Error desconocido',
      };
    }
  }

  async completeMercadoLibreOAuth(code: string): Promise<OAuthCompleteResponse> {
    try {
      if (!this.functions || !this.httpsCallable) {
        throw new Error('Firebase Functions no está disponible');
      }

      const completeOAuthMercadoLibre = this.httpsCallable(this.functions, 'completeOAuthMercadoLibre');

      const result = await completeOAuthMercadoLibre({ code });

      return result.data;
    } catch (error) {
      console.error('Error completando OAuth MercadoLibre:', error);
      return {
        success: false,
        message: 'Error completando autenticación',
        platform: 'mercadolibre',
        error: error instanceof Error ? error.message : 'Error desconocido',
      };
    }
  }

  // ============================================================================
  // SHOPIFY
  // ============================================================================

  async initShopifyOAuth(shopDomain: string): Promise<OAuthInitResponse> {
    try {
      if (!shopDomain) {
        return {
          success: false,
          message: 'El dominio de la tienda Shopify es requerido',
        };
      }

      if (!this.functions || !this.httpsCallable) {
        throw new Error('Firebase Functions no está disponible');
      }

      const initOAuthShopify = this.httpsCallable(this.functions, 'initOAuthShopify');

      // Solo enviar el dominio de la tienda
      const result = await initOAuthShopify({
        shopDomain,
        scopes: ['read_products', 'write_products', 'read_orders', 'write_orders'],
      });

      return result.data;
    } catch (error) {
      console.error('Error iniciando OAuth Shopify:', error);
      return {
        success: false,
        message: 'Error iniciando autenticación',
        error: error instanceof Error ? error.message : 'Error desconocido',
      };
    }
  }

  async completeShopifyOAuth(code: string, shop: string, state: string): Promise<OAuthCompleteResponse> {
    try {
      if (!this.functions || !this.httpsCallable) {
        throw new Error('Firebase Functions no está disponible');
      }

      const completeOAuthShopify = this.httpsCallable(this.functions, 'completeOAuthShopify');

      const result = await completeOAuthShopify({ code, shop, state });

      return result.data;
    } catch (error) {
      console.error('Error completando OAuth Shopify:', error);
      return {
        success: false,
        message: 'Error completando autenticación',
        platform: 'shopify',
        error: error instanceof Error ? error.message : 'Error desconocido',
      };
    }
  }

  // ============================================================================
  // TIENDANUBE
  // ============================================================================

  async initTiendaNubeOAuth(): Promise<OAuthInitResponse> {
    try {
      if (!this.functions || !this.httpsCallable) {
        throw new Error('Firebase Functions no está disponible');
      }

      const initOAuthTiendaNube = this.httpsCallable(this.functions, 'initOAuthTiendaNube');

      // No necesita parámetros - las credenciales están en el servidor
      const result = await initOAuthTiendaNube({});

      return result.data;
    } catch (error) {
      console.error('Error iniciando OAuth TiendaNube:', error);
      return {
        success: false,
        message: 'Error iniciando autenticación',
        error: error instanceof Error ? error.message : 'Error desconocido',
      };
    }
  }

  async completeTiendaNubeOAuth(code: string): Promise<OAuthCompleteResponse> {
    try {
      if (!this.functions || !this.httpsCallable) {
        throw new Error('Firebase Functions no está disponible');
      }

      const completeOAuthTiendaNube = this.httpsCallable(this.functions, 'completeOAuthTiendaNube');

      const result = await completeOAuthTiendaNube({ code });

      return result.data;
    } catch (error) {
      console.error('Error completando OAuth TiendaNube:', error);
      return {
        success: false,
        message: 'Error completando autenticación',
        platform: 'tiendanube',
        error: error instanceof Error ? error.message : 'Error desconocido',
      };
    }
  }

  // ============================================================================
  // INSTAGRAM
  // ============================================================================

  async initInstagramOAuth(): Promise<OAuthInitResponse> {
    try {
      if (!this.functions || !this.httpsCallable) {
        throw new Error('Firebase Functions no está disponible');
      }

      const initOAuthInstagram = this.httpsCallable(this.functions, 'initOAuthInstagram');

      // No necesita parámetros - las credenciales están en el servidor
      const result = await initOAuthInstagram({});

      return result.data;
    } catch (error) {
      console.error('Error iniciando OAuth Instagram:', error);
      return {
        success: false,
        message: 'Error iniciando autenticación',
        error: error instanceof Error ? error.message : 'Error desconocido',
      };
    }
  }

  async completeInstagramOAuth(code: string): Promise<OAuthCompleteResponse> {
    try {
      if (!this.functions || !this.httpsCallable) {
        throw new Error('Firebase Functions no está disponible');
      }

      const completeOAuthInstagram = this.httpsCallable(this.functions, 'completeOAuthInstagram');

      const result = await completeOAuthInstagram({ code });

      return result.data;
    } catch (error) {
      console.error('Error completando OAuth Instagram:', error);
      return {
        success: false,
        message: 'Error completando autenticación',
        platform: 'instagram',
        error: error instanceof Error ? error.message : 'Error desconocido',
      };
    }
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  openOAuthPopup(authUrl: string, platform: string): Window | null {
    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      authUrl,
      `oauth_${platform}`,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no`
    );

    return popup;
  }

  listenForOAuthCallback(
    platform: string,
    onSuccess: (data: OAuthCompleteResponse) => void,
    onError: (error: string) => void
  ): () => void {
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === `oauth_callback_${platform}`) {
        try {
          const { code, shop, state } = event.data;

          let result: OAuthCompleteResponse;

          switch (platform) {
            case 'mercadolibre':
              result = await this.completeMercadoLibreOAuth(code);
              break;
            case 'shopify':
              result = await this.completeShopifyOAuth(code, shop, state);
              break;
            case 'tiendanube':
              result = await this.completeTiendaNubeOAuth(code);
              break;
            case 'instagram':
              result = await this.completeInstagramOAuth(code);
              break;
            default:
              onError('Plataforma no soportada');
              return;
          }

          if (result.success) {
            onSuccess(result);
          } else {
            onError(result.error || 'Error completando autenticación');
          }
        } catch (error) {
          onError(error instanceof Error ? error.message : 'Error desconocido');
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }
}

// Crear y exportar instancia singleton
const oauthService = new OAuthService();
export default oauthService;
